/**
 * 属性区：随选中元素切换视图，供顶栏 Popover 等复用
 */
import { useMemo } from 'react';
import { cn } from '@heroui/react';
import { useCanvas } from '@/components/canvas/designer/shell/CanvasContext';
import { PageProperties } from '../../page/PageProperties';
import { WorkspaceProperties } from '../../workspace-elements/WorkspaceProperties';
import { LayerProperties } from './LayerProperties';
import { TextProperties } from '../../elements/text/TextProperties';
import { ImageProperties } from '../../elements/image/ImageProperties';
import { TableProperties } from '../../elements/table/TableProperties';
import { VectorProperties } from '../../elements/shape/ShapeProperties';
import type {
  TextElement,
  ImageElement,
  TableElement,
  LineElement,
  RectangleElement,
  CircleElement,
  TriangleElement,
  VectorElement,
  FreehandElement,
} from '@/components/canvas/document/publicTypes';
import type { Block } from '@/components/canvas/blocks/types';
import {
  BlocksProperties,
  getBlockPropertyViewId,
  isBlockPropertyViewId,
} from '@/components/canvas/blocks/BlocksProperties';
import { getBlockInspectorTitle } from '@/components/canvas/blocks/registry/blockRegistry';
import '@/components/canvas/blocks';

type ShapeElement = LineElement | RectangleElement | CircleElement | TriangleElement;

const VIEW_PAGE = 'page';
const VIEW_WORKSPACE = 'workspace';
const VIEW_TEXT = 'text';
const VIEW_IMAGE = 'image';
const VIEW_TABLE = 'table';
const VIEW_SHAPE = 'shape';
const VIEW_LAYERS = 'layers';

const STATIC_PROPERTY_VIEW_ITEMS: { id: string; title: string }[] = [
  { id: VIEW_PAGE, title: '页面' },
  { id: VIEW_WORKSPACE, title: '工作区' },
  { id: VIEW_TEXT, title: '文本' },
  { id: VIEW_IMAGE, title: '图片' },
  { id: VIEW_TABLE, title: '表格' },
  { id: VIEW_SHAPE, title: '图形' },
  { id: VIEW_LAYERS, title: '图层' },
];

function usePropertyPanelModel() {
  const {
    getCurrentPage,
    workspaceElements,
    selectedElementIds,
    selectedBlockIds,
    editingTextId,
    freehandToolActive,
  } = useCanvas();

  const page = getCurrentPage();
  const elements = page?.elements ?? workspaceElements;
  const selectedCount = selectedElementIds.length;

  const selectedTextElement = ((): TextElement | null => {
    if (editingTextId) {
      const el = elements.find((e) => e.id === editingTextId && e.type === 'text');
      if (el) return el as TextElement;
    }
    if (selectedCount === 1) {
      const el = elements.find((e) => e.id === selectedElementIds[0]);
      if (el?.type === 'text') return el as TextElement;
    }
    return null;
  })();

  const selectedImageElement = ((): ImageElement | null => {
    if (editingTextId) return null;
    if (selectedCount === 1) {
      const el = elements.find((e) => e.id === selectedElementIds[0]);
      if (el?.type === 'image') return el as ImageElement;
    }
    return null;
  })();

  const selectedTableElement = ((): TableElement | null => {
    if (editingTextId) return null;
    if (selectedCount === 1) {
      const el = elements.find((e) => e.id === selectedElementIds[0]);
      if (el?.type === 'table') return el as TableElement;
    }
    return null;
  })();

  const selectedShapeElement = ((): ShapeElement | null => {
    if (editingTextId) return null;
    if (selectedCount === 1) {
      const el = elements.find((e) => e.id === selectedElementIds[0]);
      if (
        el &&
        (el.type === 'line' || el.type === 'rect' || el.type === 'circle' || el.type === 'triangle')
      ) {
        return el as ShapeElement;
      }
    }
    return null;
  })();

  const selectedVectorElement = ((): VectorElement | null => {
    if (editingTextId) return null;
    if (selectedCount === 1) {
      const el = elements.find((e) => e.id === selectedElementIds[0]);
      if (el?.type === 'vector') return el as VectorElement;
    }
    return null;
  })();

  const selectedFreehandElement = ((): FreehandElement | null => {
    if (editingTextId) return null;
    if (selectedCount === 1) {
      const el = elements.find((e) => e.id === selectedElementIds[0]);
      if (el?.type === 'freehand') return el as FreehandElement;
    }
    return null;
  })();

  const selectedBlock = ((): Block | null => {
    if (editingTextId || selectedCount > 0) return null;
    if (selectedBlockIds.length !== 1) return null;
    return (page?.blocks ?? []).find((b) => b.id === selectedBlockIds[0]) ?? null;
  })();

  const effectiveViewFromSelection = useMemo(() => {
    if (selectedBlock && selectedCount === 0 && !editingTextId) {
      return getBlockPropertyViewId(selectedBlock.type);
    }
    if (selectedCount === 0 && !editingTextId && selectedBlockIds.length === 0) {
      if (!page) return VIEW_WORKSPACE;
      return freehandToolActive ? VIEW_SHAPE : VIEW_PAGE;
    }
    if (selectedCount >= 2) return VIEW_LAYERS;
    if (selectedShapeElement || selectedVectorElement || selectedFreehandElement) return VIEW_SHAPE;
    if (selectedImageElement) return VIEW_IMAGE;
    if (selectedTableElement) return VIEW_TABLE;
    if (selectedTextElement) return VIEW_TEXT;
    if (selectedCount === 1 || editingTextId) return VIEW_PAGE;
    return VIEW_PAGE;
  }, [
    page,
    selectedCount,
    editingTextId,
    freehandToolActive,
    selectedTextElement,
    selectedImageElement,
    selectedTableElement,
    selectedShapeElement,
    selectedVectorElement,
    selectedFreehandElement,
    selectedBlockIds,
    selectedBlock,
  ]);

  const effectiveView = effectiveViewFromSelection;

  const headerTitle = useMemo(() => {
    if (isBlockPropertyViewId(effectiveView) && selectedBlock) {
      return getBlockInspectorTitle(selectedBlock);
    }
    return STATIC_PROPERTY_VIEW_ITEMS.find((item) => item.id === effectiveView)?.title ?? '页面';
  }, [effectiveView, selectedBlock]);

  return {
    page,
    effectiveView,
    headerTitle,
    selectedTextElement,
    selectedImageElement,
    selectedTableElement,
    selectedShapeElement,
    selectedVectorElement,
    selectedFreehandElement,
    selectedBlock,
    freehandToolActive,
  };
}

export function usePropertyPanelHeaderTitle(): string {
  const { headerTitle } = usePropertyPanelModel();
  return headerTitle;
}

export const PROPERTY_PANEL_VIEW_CLASS = 'property-panel-view min-h-0 w-full shrink-0';

export interface PropertyPanelBodyProps {
  className?: string;
  omitHeader?: boolean;
}

export function PropertyPanelBody({ className, omitHeader = false }: PropertyPanelBodyProps) {
  const {
    page,
    effectiveView,
    headerTitle,
    selectedTextElement,
    selectedImageElement,
    selectedTableElement,
    selectedShapeElement,
    selectedVectorElement,
    selectedFreehandElement,
    selectedBlock,
  } = usePropertyPanelModel();

  const showBlockProperties =
    isBlockPropertyViewId(effectiveView) && selectedBlock != null;

  return (
    <div className={cn('flex min-h-0 w-full min-w-0 flex-1 flex-col overflow-hidden', className)}>
      {!omitHeader ? (
        <header className="shrink-0 border-b border-divider px-3 py-2 text-sm font-medium text-default-foreground">
          {headerTitle}
        </header>
      ) : null}
      <div
        data-property-panel-scroll
        className="property-panel-scroll min-h-0 flex-1 overflow-x-hidden overflow-y-auto overscroll-y-contain px-3 py-2"
      >
        {effectiveView === VIEW_WORKSPACE && <WorkspaceProperties />}

        {effectiveView === VIEW_PAGE && page && <PageProperties />}

        {effectiveView === VIEW_TEXT && selectedTextElement && (
          <TextProperties element={selectedTextElement} />
        )}

        {effectiveView === VIEW_IMAGE && selectedImageElement && (
          <ImageProperties element={selectedImageElement} />
        )}

        {effectiveView === VIEW_TABLE && selectedTableElement && (
          <TableProperties element={selectedTableElement} />
        )}

        {showBlockProperties && selectedBlock && <BlocksProperties block={selectedBlock} />}

        {effectiveView === VIEW_SHAPE &&
          (selectedShapeElement ?? selectedVectorElement ?? selectedFreehandElement ? (
            <VectorProperties
              element={(selectedShapeElement ?? selectedVectorElement ?? selectedFreehandElement)!}
            />
          ) : (
            <VectorProperties freehandToolDefaults />
          ))}

        {effectiveView === VIEW_LAYERS && <LayerProperties />}
      </div>
    </div>
  );
}
